import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BullhornIcon, GearIcon, MessagesQuestionIcon } from '@revfluence/fresh-icons/solid/esm';
import { ArrowLeftFromLineIcon } from '@revfluence/fresh-icons/solid';
import { useQuery } from '@apollo/client';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/shadcn/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/shadcn/components/ui/avatar';
import { Muted } from '@/shadcn/components/typography/muted';
import { Small } from '@/shadcn/components/typography/small';
import { Button } from '@/shadcn/components/ui/button';
import { GET_CLIENT_LOGOS_QUERY } from '@/app/queries/GetClientLogoQuery';

export const UserMenu = ({
  user,
  currentClient,
  profile,
  onSwitchClient,
  logout,
  onOpenChange,
}) => {
  const { data: clientLogosData } = useQuery(GET_CLIENT_LOGOS_QUERY, {
    variables: {
      clientIds: profile.clients.map((client) => client.id)
    },
    fetchPolicy: 'cache-first',
    skip: !profile.clients,
  });

  const clientLogos = useMemo(() => {
    if (!clientLogosData?.getBackendServerClientsByClientIds) return {};
    return clientLogosData.getBackendServerClientsByClientIds.reduce((acc, client) => {
      acc[client.id] = client.logo_url;
      return acc;
    }, {});
  }, [clientLogosData]);

  const currentLogo = clientLogos[currentClient?.id];

  return (
    <DropdownMenu onOpenChange={(open) => {
      if (open) {
        onOpenChange?.(false); // Close the sidebar when menu opens
      }
    }}
    >
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" className="overflow-hidden rounded-full">
          <Avatar>
            <AvatarImage src={user.picture} alt={user.name} className="object-cover" />
            <AvatarFallback>
              {user?.name
                .split(' ')
                .map((n) => n[0])
                .join('')}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[300px] ml-[85px] rounded-2xl !p-0 ">
        <div className="p-2 rounded-2xl">
          <div className="bg-grey-1 p-4 rounded-xl">
            <DropdownMenuLabel className="py-0 px-0">
              <div className="flex flex-col gap-1">
                <Small className="text-primary text-base font-medium">{user.name}</Small>
                <Muted className="text-sm text-grey-5 font-normal">{profile.email}</Muted>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="my-4 bg-grey-2" />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={currentLogo} alt={currentClient?.name} />
                  <AvatarFallback className="bg-grey-2 text-primary">{currentClient?.name?.[0]}</AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium">{currentClient?.name}</span>
              </div>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="text-xs font-medium text-grey-5 px-0">
                  Switch
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-[300px] rounded-2xl ml-[28px] max-h-[330px] overflow-y-auto [&::-webkit-scrollbar]{width:6px} [&::-webkit-scrollbar-track]{background:transparent} [&::-webkit-scrollbar-thumb]{background-color:#E5E7EB;border-radius:9999px}">
                  <div className="sticky top-[-5px] bg-white z-10 py-2 px-2 border-b border-grey-2">
                    <Small className="text-xs font-medium text-grey-5">Switch Account</Small>
                  </div>
                  {profile.clients.map((client) => {
                    return (
                      <DropdownMenuItem
                        key={client.id}
                        onSelect={() => onSwitchClient(client)}
                        className="flex items-center gap-2 py-2 hover:bg-grey-1 cursor-pointer font-medium rounded-xl"
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={clientLogos[client.id]} alt={client.name} />
                          <AvatarFallback className="bg-grey-2 text-primary">{client.name?.[0] || '?'}</AvatarFallback>
                        </Avatar>
                        <span className="text-sm">{client.name}</span>
                        {client.id === currentClient?.id && (
                          <span className="ml-auto">✓</span>
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </div>
          </div>
          <DropdownMenuItem asChild className="gap-2 py-2 cursor-pointer text-sm text-primary rounded-xl font-medium hover:bg-grey-1">
            <Link to="/settings/account">
              <GearIcon className="h-4 w-4 text-grey-5" />
              <span className="text-sm">Settings</span>
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem asChild className="gap-2 py-2 cursor-pointer text-sm text-primary rounded-xl font-medium hover:bg-grey-1">
            <a href="https://help.aspireiq.com/" target="_blank" rel="noopener noreferrer">
              <MessagesQuestionIcon className="h-4 w-4 text-grey-5" />
              <span className="text-sm">Help Center</span>
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="gap-2 py-2 cursor-pointer text-sm text-primary rounded-xl font-medium hover:bg-grey-1">
            <a href="https://productupdates.aspireiq.com/" target="_blank" rel="noopener noreferrer">
              <BullhornIcon className="h-4 w-4 text-grey-5" />
              <span className="text-sm">Product Updates</span>
            </a>
          </DropdownMenuItem>

          <DropdownMenuItem onSelect={logout} className="gap-2 py-2 text-sm cursor-pointer text-primary rounded-xl font-medium hover:bg-grey-1">
            <ArrowLeftFromLineIcon className="h-4 w-4 text-grey-5" />
            Log Out
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
